import type { LineItem } from '~/types/shopware'

type MediaLike = { url?: string; alt?: string; title?: string }
type LineItemPayload = { productId?: string; id?: string }

export const useCartLineItemUi = (cartItem: Ref<LineItem>) => {
  const { useProductIdSeoPath } = useSeoUrls()
  const { codeErrorsNotification } = useCartNotification()
  const { getFormattedPrice } = usePrice()

  const { removeItem, itemTotalPrice, itemQuantity, isPromotion, changeItemQuantity } = useCartItem(cartItem)

  const isLoading = ref(false)
  const quantity = ref<number | undefined>()
  syncRefs(itemQuantity, quantity)

  const updateQuantity = async (val: number | undefined) => {
    if (val === itemQuantity.value) return
    isLoading.value = true
    await changeItemQuantity(Number(val))
    codeErrorsNotification()
    isLoading.value = false
  }
  const debounceUpdate = useDebounceFn(updateQuantity, 800)
  watch(quantity, () => debounceUpdate(quantity.value))

  const productId = computed<string | undefined>(() => {
    const li = cartItem.value as LineItem & { payload?: LineItemPayload }
    if (li?.type !== 'product') return undefined
    return li.referencedId || li.payload?.productId || li.payload?.id
  })

  const itemUrl = useProductIdSeoPath(productId)

  const normalizeMedia = (src: unknown): MediaLike | undefined => {
    if (!src || typeof src !== 'object') return undefined
    const obj = src as { url?: unknown; alt?: unknown; title?: unknown; media?: unknown }
    if (
      (typeof obj.url === 'string' && obj.url.length) ||
      typeof obj.alt === 'string' ||
      typeof obj.title === 'string'
    ) {
      return {
        url: typeof obj.url === 'string' ? obj.url : undefined,
        alt: typeof obj.alt === 'string' ? obj.alt : undefined,
        title: typeof obj.title === 'string' ? obj.title : undefined,
      }
    }
    if (obj.media && typeof obj.media === 'object') {
      const m = obj.media as { url?: unknown; alt?: unknown; title?: unknown }
      return {
        url: typeof m.url === 'string' ? m.url : undefined,
        alt: typeof m.alt === 'string' ? m.alt : undefined,
        title: typeof m.title === 'string' ? m.title : undefined,
      }
    }
    return undefined
  }

  const cover = computed<MediaLike | undefined>(() => {
    const item = cartItem.value as LineItem & { media?: unknown; cover?: unknown }
    return normalizeMedia(item.cover) || normalizeMedia(item.media)
  })

  const removeCartItem = async () => {
    isLoading.value = true
    await removeItem()
    isLoading.value = false
  }

  return {
    // core state
    isLoading,
    quantity,
    productId,
    itemUrl,
    cover,
    // prices & flags
    itemTotalPrice,
    isPromotion,
    getFormattedPrice,
    // actions
    removeCartItem,
  }
}
